/** Плашка доверия: сходятся ли начисления периода с балансом ЛК (сверка витрины mart.mp_finance_recon). */
import type { WbRecon } from "@/api/mpAnalytics";
import type { OzRecon } from "@/api/ozonAnalytics";
import { monthLabel, rub } from "./format";

export interface Trust {
  /** ok — все месяцы периода сверены по каждому юрлицу; mismatch — есть необъяснённое
   *  расхождение; missing — по части (месяц × юрлицо) сверки нет (например, текущий месяц:
   *  баланс ЛК помесячный) */
  kind: "ok" | "mismatch" | "missing";
  text: string;
}

/** Статус по месяцам периода `months` (YYYY-MM) и юрлицам `legals` (выбранное или все из /meta).
 *  Месяц «сверен», только если сверка есть по КАЖДОМУ юрлицу. Расхождение важнее отсутствия. */
export function trustStatus(recon: OzRecon, months: string[], legals: string[]): Trust {
  const key = (m: string, l: string): string => `${m}\u0001${l}`;
  const rows = new Map(recon.months.map((r) => [key(r.month, r.legal_name), r]));
  const bad = months.filter((m) => legals.some((l) => rows.get(key(m, l))?.status === "mismatch"));
  if (bad.length) {
    const parts = bad.map((m) => {
      const abs = legals.reduce((s, l) => s + (rows.get(key(m, l))?.unexplained_abs ?? 0), 0);
      return `${rub(abs)} за ${monthLabel(m)}`;
    });
    return { kind: "mismatch", text: `Расхождение с балансом ЛК: ${parts.join(", ")}` };
  }
  // `no_header` — баланса ЛК за месяц нет: не «сверено» и не «расхождение»
  const full = months.filter((m) => legals.every((l) => rows.get(key(m, l))?.status === "ok"));
  const missing = months.flatMap((m) =>
    legals.flatMap((l) => {
      const r = rows.get(key(m, l));
      if (!r) return [`по ${l} за ${monthLabel(m)}`];
      return r.status === "no_header" ? [`по ${l} за ${monthLabel(m)} (нет баланса ЛК)`] : [];
    }),
  );
  const explained = full.reduce(
    (s, m) => s + legals.reduce((a, l) => a + (rows.get(key(m, l))?.explained ?? 0), 0),
    0,
  );
  const known = Math.abs(explained) >= 0.005 ? ` (известные ошибки Ozon: ${rub(explained)})` : "";
  if (missing.length) {
    const ok = full.length ? `сверено: ${full.map(monthLabel).join(", ")}${known} · ` : "";
    return { kind: "missing", text: `${ok}нет сверки ${missing.join(", ")}` };
  }
  return { kind: "ok", text: `Сверено с балансом ЛК${known}` };
}

/** Плашка доверия WB: сходятся ли начисления с шапками отчётов WB (`/mp-analytics/wb/recon`;
 *  месяц сверки — по началу отчёта). Статус месяца × юрлица: ok — сверено, mismatch — расхождение
 *  (важнее всего), no_header — шапки WB нет («не с чем сверить»), pending — ждёт недельного отчёта,
 *  строки нет — сверки нет. */
export function wbTrustStatus(recon: WbRecon, months: string[], legals: string[]): Trust {
  const key = (m: string, l: string): string => `${m}\u0001${l}`;
  const rows = new Map(recon.months.map((r) => [key(r.month, r.legal_name), r]));
  const bad = months.filter((m) => legals.some((l) => rows.get(key(m, l))?.status === "mismatch"));
  if (bad.length) {
    const parts = bad.map((m) => {
      const abs = legals.reduce((s, l) => {
        const r = rows.get(key(m, l));
        return s + (r?.status === "mismatch" ? r.unexplained_abs : 0);
      }, 0);
      return `${rub(abs)} за ${monthLabel(m)}`;
    });
    return { kind: "mismatch", text: `Расхождение с отчётами WB: ${parts.join(", ")}` };
  }
  const full = months.filter((m) => legals.every((l) => rows.get(key(m, l))?.status === "ok"));
  const gaps = months.flatMap((m) =>
    legals.flatMap((l) => {
      const r = rows.get(key(m, l));
      if (!r) return [`нет сверки по ${l} за ${monthLabel(m)}`];
      if (r.status === "no_header") return [`не с чем сверить по ${l} за ${monthLabel(m)}`];
      if (r.status === "pending") return [`ждёт недельного отчёта по ${l} за ${monthLabel(m)}`];
      return [];
    }),
  );
  if (gaps.length) {
    const ok = full.length ? `сверено: ${full.map(monthLabel).join(", ")} · ` : "";
    return { kind: "missing", text: `${ok}${gaps.join(", ")}` };
  }
  return { kind: "ok", text: "Сверено с отчётами WB" };
}
